import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MoreHorizontal, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { navItems, bottomBarKeys } from "./navItems";
import "./index.css";
import logo from "../../static/images/logo.png";
import logoCollapsed from "../../static/images/logo_small.png";
import { useAuth } from "../../context/AuthProvider";
import { fmtCurrencyBR } from "../../utils/formatBrl";
import { getVisibleBots } from "../../utils/bots";

export default function Sidebar({ collapsed = false, onToggleCollapse }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const bots = getVisibleBots(user?.bots);
  const isAdmin = user?.is_admin === true;

  const hasBots = bots.length > 0;

  // ID do último bot selecionado (persistido em localStorage)
  const [lastSelectedBotId, setLastSelectedBotId] = useState(() => {
    try {
      return localStorage.getItem("selectedBotId");
    } catch {
      return null;
    }
  });

  // Garante que o ID salvo ainda existe na lista de bots visíveis
  useEffect(() => {
    if (!bots.length) {
      if (lastSelectedBotId) {
        setLastSelectedBotId(null);
      }
      return;
    }

    const exists = bots.some(b => String(b.id) === String(lastSelectedBotId));
    if (!exists) {
      setLastSelectedBotId(String(bots[0].id));
    }
  }, [bots]);

  function navigateWithBot(key) {
    if (!bots.length) return;

    // Relê a seleção mais recente direto do localStorage: ela pode ter sido
    // alterada em outra página (BotManagement, Fluxos, editor de fluxo) depois
    // que a Sidebar montou, então o estado local pode estar desatualizado.
    let storedId = null;
    try {
      storedId = localStorage.getItem("selectedBotId");
    } catch { }

    const target =
      bots.find(b => String(b.id) === String(storedId)) || bots[0];

    try {
      localStorage.setItem("selectedBotId", target.id);
      setLastSelectedBotId(String(target.id));
    } catch { }

    navigate(`/bots/${key}/${target.id}`);
    setSidebarOpen(false);
  }

  function handleNavClick(item, e) {
    e.preventDefault();
    const needsBot = !!item.requiresBot;
    if (needsBot && !hasBots) return;

    if (item.key === "statistics") {
      navigate(item.path);
    } else if (needsBot) {
      navigateWithBot(item.key);
    } else {
      navigate(item.path);
    }

    setSidebarOpen(false);
  }

  const bottomBarItems = bottomBarKeys
    .map(key => navItems.find(item => item.key === key))
    .filter(Boolean);

  return (
    <>
      <div
        className={`sidebar-overlay ${sidebarOpen ? "active" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />

      <aside
        className={`sidebar ${sidebarOpen ? "active" : ""} ${collapsed ? "collapsed" : "expanded"
          }`}
      >
        <div className="sidebar-header flex items-center justify-center">
          <img
            src={collapsed ? logoCollapsed : logo}
            alt="Spectrum"
            width={collapsed ? 40 : 200}
          />

        </div>

        <nav className="sidebar-nav">
          <ul>
            {navItems.map(item => {
              const active =
                item.path && location.pathname.startsWith(item.path);
              const needsBot = !!item.requiresBot;
              const disabled = needsBot && !hasBots;

              return (
                <li
                  key={item.key}
                  className={`nav-item ${active ? "active" : ""}`}
                >
                  <a
                    href="#"
                    className={`nav-link ${disabled ? "disabled" : ""} `}
                    onClick={(e) => handleNavClick(item, e)}
                  >
                    {item.iconComponent}
                    <span>{item.label}</span>
                    <span className={`is-new ${item.is_new ? "visible" : "hidden"}`}>Novo</span>

                  </a>
                </li>
              );
            })}
          </ul>
        </nav>




        <button
          type="button"
          className="sidebar-collapse-toggle w-full"
          onClick={() => onToggleCollapse && onToggleCollapse(!collapsed)}
          aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
        >
          {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          {!collapsed && <span className="ml-2">Recolher menu</span>}
        </button>
      </aside>

      {/* Bottom bar mobile — substitui o menu hambúrguer */}
      <nav className="bottom-bar" aria-label="Navegação principal">
        {bottomBarItems.map(item => {
          const active = item.path && location.pathname.startsWith(item.path);
          const needsBot = !!item.requiresBot;
          const disabled = needsBot && !hasBots;

          return (
            <a
              key={item.key}
              href="#"
              className={`bottom-bar-item ${active ? "active" : ""} ${disabled ? "disabled" : ""}`}
              onClick={(e) => handleNavClick(item, e)}
            >
              {item.iconComponent}
              <span>{item.bottomBarLabel || item.label}</span>
            </a>
          );
        })}
        <button
          type="button"
          className={`bottom-bar-item ${sidebarOpen ? "active" : ""}`}
          onClick={() => setSidebarOpen(true)}
          aria-label="Mais opções"
        >
          <MoreHorizontal size={16} />
          <span>Mais</span>
        </button>
      </nav>
    </>
  );
}
